/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSuspenseQueryGet } from "../typed-client/hooks/use-suspense-query-get"
import type * as Api from "../@client/api"
import { ComboboxData, ComboboxItem, Select, SelectProps } from "@mantine/core"
import { useQueryGet } from "../typed-client/hooks/use-query-get"

type Unboxed<T> = T extends (infer U)[] ? U : T

export function DEPRECATEDRemoteSelect<
  ActionName extends keyof Api.GET,
  T = string,
>({
  url,
  map,
  toValue,
  fromValue,
  onChange,
  value,
  ...props
}: {
  url: ActionName
  map: (value: Unboxed<Api.Output[ActionName]>) => ComboboxItem
  toValue: (
    value: string,
    data: Unboxed<Api.Output[ActionName]>[]
  ) => T | null | undefined
  fromValue: (value: T | null | undefined) => string
  onChange: (value: T | null | undefined) => void
  value?: T | null | undefined
} & Omit<SelectProps, "data" | "onChange" | "value" | "multiple">) {
  const { data } = useSuspenseQueryGet(url, null as any)

  const entities = data as Unboxed<Api.Output[ActionName]>[]
  const options = entities.map ? entities.map(map) : []

  return (
    <Select
      {...props}
      multiple={false}
      value={value ? fromValue(value) : null}
      onChange={(e) => {
        const value = e === null ? null : toValue(e, entities)
        onChange(value)
      }}
      data={options}
    />
  )
}

interface Props<ActionName extends keyof Api.GET> extends Omit<
  SelectProps,
  "onChange"
> {
  url: ActionName
  input: Api.Input[ActionName]
  valueToEntity: (
    value: string | null,
    data: Api.Output[ActionName]
  ) => Unboxed<Api.Output[ActionName]> | undefined
  entitiesToComboboxData: (data: Api.Output[ActionName]) => ComboboxData
  onChange: (value: Unboxed<Api.Output[ActionName]> | undefined) => void
  value?: string
}

export function RemoteSelect<ActionName extends keyof Api.GET>({
  url,
  input,
  valueToEntity,
  entitiesToComboboxData,
  onChange,
  value,
  ...props
}: Props<ActionName>) {
  const { data: entities } = useSuspenseQueryGet(url, input)

  return (
    <Select
      multiple={false}
      value={value}
      onChange={(e) => {
        onChange(valueToEntity(e, entities))
      }}
      data={entitiesToComboboxData(entities)}
      {...props}
    />
  )
}

export type SelectEntityProps<ActionName extends keyof Api.GET> = {
  url: ActionName
  toRequest?: (search: string) => Api.Input[ActionName]
  getLabel: (entity: Unboxed<Api.Output[ActionName]>) => string
  getId: (entity: Unboxed<Api.Output[ActionName]>) => number
  onEntitiesLoading?: (entities?: Unboxed<Api.Output[ActionName]>[]) => void
  onChange: (
    value: number | null | undefined,
    entity: Unboxed<Api.Output[ActionName]>
  ) => void
  value?: number | null | undefined
  disableMantineFilter?: boolean
  getDefaultValue?: (data: ComboboxItem[]) => string | undefined
  autoFocus?: boolean
} & Omit<SelectProps, "data" | "onChange" | "value" | "multiple">

export function SelectEntity<ActionName extends keyof Api.GET>({
  url,
  toRequest,
  onChange,
  value,
  getLabel,
  getId,
  disableMantineFilter,
  getDefaultValue,
  ...props
}: SelectEntityProps<ActionName>) {
  const [search, setSearch] = React.useState("")
  const input = React.useMemo(() => toRequest && toRequest(search), [search])
  const { data } = useQueryGet(
    url,
    typeof input === undefined ? (null as any) : input
  )
  const entities = (data ? data : []) as Unboxed<Api.Output[ActionName]>[]
  React.useEffect(() => {
    props.onEntitiesLoading?.(
      data as Unboxed<Api.Output[ActionName]>[] | undefined
    )
  }, [data])

  const options = entities.map
    ? entities.map((v) => ({
        label: getLabel(v),
        value: getId(v).toString(),
      }))
    : []

  React.useEffect(() => {
    if (!value && getDefaultValue) {
      const defaultValue = getDefaultValue(options)
      if (defaultValue) {
        onChange(parseInt(defaultValue), entities[0])
      }
    }
  }, [getDefaultValue, options, entities])

  return (
    <Select
      {...props}
      onSearchChange={setSearch}
      filter={disableMantineFilter ? () => options : undefined}
      defaultSearchValue={search}
      multiple={false}
      value={value?.toString() ?? null}
      onChange={(e) => {
        if (e === null) {
          onChange(null, null as any)
          return
        }
        const id = parseInt(e)
        const entity = entities.find((v) => getId(v) === id)
        if (!entity) {
          throw new Error("Entity not found")
        }
        onChange(id, entity)
      }}
      data={options}
    />
  )
}

export const UserSelectWithRef = React.forwardRef(function UserSelectWithRef<
  ActionName extends keyof Api.GET,
>(
  {
    url,
    toRequest,
    onChange,
    value,
    getLabel,
    getId,
    disableMantineFilter,
    getDefaultValue,
    ...props
  }: SelectEntityProps<ActionName>,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const [search, setSearch] = React.useState("")
  const input = React.useMemo(() => toRequest && toRequest(search), [search])
  const { data } = useQueryGet(
    url,
    typeof input === undefined ? (null as any) : input
  )
  const entities = (data ? data : []) as Unboxed<Api.Output[ActionName]>[]
  React.useEffect(() => {
    props.onEntitiesLoading?.(
      data as Unboxed<Api.Output[ActionName]>[] | undefined
    )
  }, [data])

  const options = entities.map
    ? entities.map((v) => ({
        label: getLabel(v),
        value: getId(v).toString(),
      }))
    : []

  React.useEffect(() => {
    if (!value && getDefaultValue) {
      const defaultValue = getDefaultValue(options)
      if (defaultValue) {
        onChange(parseInt(defaultValue), entities[0])
      }
    }
  }, [getDefaultValue, options, entities])

  return (
    <Select
      ref={ref}
      {...props}
      onSearchChange={setSearch}
      filter={disableMantineFilter ? () => options : undefined}
      defaultSearchValue={search}
      multiple={false}
      value={value ? value.toString() : null}
      onChange={(e) => {
        if (!e) {
          onChange(null, null as any)
          return
        }
        const id = parseInt(e)
        const entity = entities.find((v) => getId(v) === id)
        if (!entity) {
          throw new Error("Entity not found")
        }
        onChange(id, entity)
      }}
      data={options}
    />
  )
})
